import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getGitHubStats } from '../../../services/githubService';

// This component will attempt to fetch live GitHub stats when possible and
// fall back to the `stats` prop if provided or to placeholders.

const GitHubStats = ({ stats: initialStats }) => {
  const [stats, setStats] = useState(initialStats || {});

  useEffect(() => {
    let mounted = true;
    async function fetchStats() {
      try {
        const data = await getGitHubStats();
        if (!mounted || !data) return;

        const nodes = data.repositories?.nodes || [];
        const totalStars = nodes.reduce((s, n) => s + (n.stargazerCount || 0), 0);
        const totalForks = nodes.reduce((s, n) => s + (n.forkCount || 0), 0);

        const mapped = {
          totalCommits: data.contributionsCollection?.totalCommitContributions ?? initialStats?.totalCommits,
          publicRepos: data.repositories?.totalCount ?? initialStats?.publicRepos,
          totalStars,
          totalForks,
          recentActivity: [],
        };

        setStats(mapped);
      } catch (err) {
        console.error('Failed to fetch GitHub stats for portfolio:', err?.message || err);
      }
    }

    fetchStats();
    return () => { mounted = false; };
  }, [initialStats]);

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="Github" size={24} className="text-foreground" />
        <h2 className="text-xl font-semibold text-foreground">GitHub Activity</h2>
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Active</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="GitCommit" size={24} className="text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground">{stats?.totalCommits}</div>
          <div className="text-sm text-muted-foreground">Total Commits</div>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Folder" size={24} className="text-accent" />
          </div>
          <div className="text-2xl font-bold text-foreground">{stats?.publicRepos}</div>
          <div className="text-sm text-muted-foreground">Public Repos</div>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Star" size={24} className="text-yellow-600" />
          </div>
          <div className="text-2xl font-bold text-foreground">{stats?.totalStars}</div>
          <div className="text-sm text-muted-foreground">Total Stars</div>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="GitFork" size={24} className="text-green-600" />
          </div>
          <div className="text-2xl font-bold text-foreground">{stats?.totalForks}</div>
          <div className="text-sm text-muted-foreground">Total Forks</div>
        </div>
      </div>
      {/* Contribution Graph Placeholder */}
      <div className="bg-muted rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Contribution Activity</h3>
        <div className="grid grid-cols-12 gap-1">
          {Array.from({ length: 84 }, (_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-sm ${
                Math.random() > 0.7 ? 'bg-green-500' :
                Math.random() > 0.5 ? 'bg-green-300' :
                Math.random() > 0.3 ? 'bg-green-200': 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>Less</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-200 rounded-sm" />
            <div className="w-3 h-3 bg-green-200 rounded-sm" />
            <div className="w-3 h-3 bg-green-300 rounded-sm" />
            <div className="w-3 h-3 bg-green-500 rounded-sm" />
          </div>
          <span>More</span>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Recent Activity</h3>
        <div className="space-y-3">
          {stats?.recentActivity?.map((activity, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <Icon name={activity?.type === 'commit' ? 'GitCommit' : activity?.type === 'star' ? 'Star' : 'GitFork'} size={16} className="text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-foreground">{activity?.message}</p>
                <p className="text-xs text-muted-foreground">{activity?.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GitHubStats;
